import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { AdminRefreshButton } from '../../components/primitives/admin-refresh-button.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { formatCount, formatDate } from '../../lib/format.js';
import { useAdminProducts, useAdminProductMutations } from '../hooks/use-products.js';
import { useAdminCategories } from '../hooks/use-categories.js';
import { useAdminBrands } from '../hooks/use-brands.js';
import { BulkActionToolbar } from '../components/bulk-action-toolbar.js';
import type { AdminProductListItem, BulkProductAction, ProductListParams } from '../types/catalog.js';

const PAGE_SIZE = 20;

const STATUS_TONE: Record<string, 'neutral' | 'success' | 'warning'> = {
  draft: 'warning',
  active: 'success',
  archived: 'neutral',
};

function stockLabel(product: AdminProductListItem): string {
  if (product.activeVariantCount === 0) return 'No variants';
  if (product.totalAvailableUnits <= 0) return 'Out of stock';
  return `${formatCount(product.totalAvailableUnits)} units`;
}

export function ProductsListPage() {
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [featured, setFeatured] = useState('');
  const [stock, setStock] = useState('');
  const [sortField, setSortField] = useState<ProductListParams['sortField']>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const params: ProductListParams = useMemo(
    () => ({
      search: search || undefined,
      status: status ? [status as never] : undefined,
      categoryId: categoryId || undefined,
      brandId: brandId || undefined,
      isFeatured: featured === '' ? undefined : featured === 'true',
      inStockOnly: stock === 'in_stock' ? true : undefined,
      sortField,
      sortDirection,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, status, categoryId, brandId, featured, stock, sortField, sortDirection, page],
  );

  const { data, isLoading, isError, refetch, isFetching } = useAdminProducts(params);
  const { data: categories } = useAdminCategories();
  const { data: brands } = useAdminBrands();
  const { archive, activate, bulkUpdate } = useAdminProductMutations();

  const rows = data ?? [];

  function resetToFirstPage() {
    setPage(0);
    setSelectedIds(new Set());
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds((prev) => {
      if (rows.length > 0 && rows.every((row) => prev.has(row.id))) return new Set();
      return new Set(rows.map((row) => row.id));
    });
  }

  function runBulkAction(action: BulkProductAction) {
    bulkUpdate.mutate(action, {
      onSuccess: () => setSelectedIds(new Set()),
    });
  }

  const columns: AdminDataTableColumn<AdminProductListItem>[] = [
    {
      key: 'name',
      header: 'Product',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.primaryImageUrl ? (
            <img src={row.primaryImageUrl} alt="" className="h-10 w-10 rounded-lg object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-gray-100" aria-hidden="true" />
          )}
          <div className="min-w-0">
            <Link
              to={`${ROUTE_NAMES.adminProducts}/${row.id}`}
              className="block truncate font-medium text-gray-900 hover:underline"
            >
              {row.name}
            </Link>
            <p className="truncate text-xs text-gray-400">{row.slug}</p>
          </div>
        </div>
      ),
    },
    { key: 'categoryName', header: 'Category', render: (row) => row.categoryName },
    { key: 'brandName', header: 'Brand', render: (row) => row.brandName ?? '—' },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <AdminStatusBadge label={row.status} tone={STATUS_TONE[row.status] ?? 'neutral'} />,
    },
    {
      key: 'isFeatured',
      header: 'Featured',
      render: (row) => (row.isFeatured ? <AdminStatusBadge label="Featured" tone="success" /> : '—'),
    },
    { key: 'activeVariantCount', header: 'Variants', render: (row) => formatCount(row.activeVariantCount) },
    { key: 'stock', header: 'Stock', render: (row) => stockLabel(row) },
    {
      key: 'updatedAt',
      header: 'Updated',
      sortable: false,
      render: (row) => formatDate(row.updatedAt),
    },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Products"
        description="Manage the product catalog -- create, edit, and control publish status."
        breadcrumbs={[{ label: 'Products' }]}
        actions={
          <>
            <AdminRefreshButton onRefresh={() => void refetch()} isRefreshing={isFetching} />
            <Link
              to={ROUTE_NAMES.adminProductNew}
              className="bg-brand text-brand-foreground inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New product
            </Link>
          </>
        }
      />

      <AdminSectionCard title="All products" className="p-4 sm:p-5">
        <AdminFilterBar>
          <AdminSearchInput
            value={searchInput}
            onChange={(value) => {
              setSearchInput(value);
              resetToFirstPage();
            }}
            placeholder="Search products…"
            label="Search products"
          />
          <AdminSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              resetToFirstPage();
            }}
            allLabel="All statuses"
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'active', label: 'Active' },
              { value: 'archived', label: 'Archived' },
            ]}
          />
          <AdminSelect
            label="Category"
            value={categoryId}
            onChange={(value) => {
              setCategoryId(value);
              resetToFirstPage();
            }}
            allLabel="All categories"
            options={(categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
          <AdminSelect
            label="Brand"
            value={brandId}
            onChange={(value) => {
              setBrandId(value);
              resetToFirstPage();
            }}
            allLabel="All brands"
            options={(brands ?? []).map((b) => ({ value: b.id, label: b.name }))}
          />
          <AdminSelect
            label="Featured"
            value={featured}
            onChange={(value) => {
              setFeatured(value);
              resetToFirstPage();
            }}
            allLabel="Featured: any"
            options={[
              { value: 'true', label: 'Featured only' },
              { value: 'false', label: 'Not featured' },
            ]}
          />
          <AdminSelect
            label="Stock"
            value={stock}
            onChange={(value) => {
              setStock(value);
              resetToFirstPage();
            }}
            allLabel="Stock: any"
            options={[{ value: 'in_stock', label: 'In stock only' }]}
          />
        </AdminFilterBar>

        <BulkActionToolbar
          selectedCount={selectedIds.size}
          categories={categories ?? []}
          brands={brands ?? []}
          productIds={[...selectedIds]}
          isRunning={bulkUpdate.isPending}
          onRun={(action) => runBulkAction(action)}
          onClear={() => setSelectedIds(new Set())}
        />

        <AdminDataTable
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          emptyTitle="No products yet"
          emptyDescription="Create your first product to get started."
          sort={{ field: sortField ?? 'createdAt', direction: sortDirection }}
          onSortChange={(field) => {
            if (field === sortField) {
              setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
            } else {
              setSortField(field as ProductListParams['sortField']);
              setSortDirection('asc');
            }
          }}
          selectable
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          rowActions={(row) => (
            <div className="flex justify-end gap-1">
              {row.status !== 'active' && (
                <button
                  type="button"
                  onClick={() => activate.mutate(row.id)}
                  className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                >
                  Activate
                </button>
              )}
              {row.status !== 'archived' && (
                <button
                  type="button"
                  onClick={() => archive.mutate(row.id)}
                  className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                >
                  Archive
                </button>
              )}
            </div>
          )}
        />

        <nav aria-label="Pagination" className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </button>
          <span>Page {page + 1}</span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < PAGE_SIZE}
            className="focus-visible:ring-brand flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 disabled:opacity-40"
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </nav>
      </AdminSectionCard>
    </AdminContent>
  );
}
